import { useMemo } from 'react';
import { useClientSlideshow } from 'ts/client/common';
import {
    hasBufferedInitialSlides,
    useSlideshowImagePreload
} from 'ts/common/components/slideshows/slideshowImagePreloader';
import { useClientGalleryServiceContext } from 'ts/common/contexts/ClientGalleryServiceContext';
import type { ICoverSlideshowContextValue } from 'ts/common/contexts/CoverSlideshowContext';
import type { ICoverPhotoBase } from 'ts/common/types';

export interface ICoverSlideshowLoaderResult {
    /** The loaded slideshow to hand to `CoverSlideshowProvider`, or null when there isn't one. */
    coverSlideshow: ICoverSlideshowContextValue | null;
    /**
     * Whether the caller may stop waiting. True when there is no cover slideshow to wait for at
     * all, when it is fully ready to play, or when loading it failed (so a broken slideshow falls
     * back to the static cover photo rather than holding the loading state forever).
     */
    isSettled: boolean;
}

/** Stable no-op used when `isSilent` — nothing ever calls it (see `CoverSlideshowBackground`'s own
 *  `isSilent` handling), so this is defense-in-depth against a future caller wiring it up anyway. */
const NO_OP_AUDIO_ACTIVE_CHANGE = () => undefined;

/**
 * Loads an event's cover slideshow so the intro page (or a header, see `isSilent` below) can wait
 * for it.
 *
 * Only call this when the cover really is a slideshow — `CoverSlideshowGate` (and its header
 * counterpart) is what decides that, and mounting it conditionally is deliberate: loading a
 * slideshow reads the client gallery's event data, which a plain photo cover has no reason to
 * require.
 */
function useCoverSlideshowLoader(
    coverPhoto: ICoverPhotoBase | false,
    { albumId, isSilent }: { albumId?: number | string | null; isSilent?: boolean } = {}
): ICoverSlideshowLoaderResult {
    const services = useClientGalleryServiceContext();
    const slideshowPhotoId =
        services && coverPhoto && coverPhoto.mediaType === 'slideshow'
            ? coverPhoto.slideshowPhotoId ?? null
            : null;

    const {
        response,
        loading,
        watermarkUrl,
        isWatermarkReady,
        handleAudioActiveChange,
        volume,
        isMuted
    } = useClientSlideshow({
        photoId: slideshowPhotoId,
        albumId,
        isSilent,
        preloadedSlideshow:
            slideshowPhotoId !== null && coverPhoto ? coverPhoto.slideshow ?? null : null,
        spAuthorizationToken: services?.spAuthorizationToken ?? EMPTY_AUTHORIZATION_TOKEN,
        spEventBus: services?.spEventBus ?? EMPTY_EVENT_BUS
    });

    const isSlideshowResponse = response?.type === 'event-slideshow' && response.slides.length > 0;
    const slides = useMemo(
        () => (isSlideshowResponse ? response.slides : []),
        [isSlideshowResponse, response]
    );
    const readyIndices = useSlideshowImagePreload(slides);

    const hasBufferedInitial = useMemo(
        () => hasBufferedInitialSlides(readyIndices, slides.length),
        [readyIndices, slides.length]
    );

    const isReady = isSlideshowResponse && isWatermarkReady && hasBufferedInitial;
    // A finished request that didn't yield a playable slideshow can never become ready, so treat
    // it as settled and let the caller fall back to the static cover photo.
    const hasFailed = !loading && response !== null && !isSlideshowResponse;

    const coverSlideshow = useMemo<ICoverSlideshowContextValue | null>(() => {
        // `slideshowPhotoId === null` covers both "this cover isn't a slideshow" and "we opted
        // out" (reduced motion, no services) — checked explicitly rather than inferred from the
        // absence of a response, so no stale response can ever leak through as a cover slideshow.
        if (slideshowPhotoId === null || !isReady || !isSlideshowResponse) {
            return null;
        }

        return {
            slideshow: response,
            watermarkUrl,
            volume: isSilent ? 0 : volume,
            isMuted: isSilent || isMuted,
            handleAudioActiveChange: isSilent ? NO_OP_AUDIO_ACTIVE_CHANGE : handleAudioActiveChange,
            isSilent
        };
    }, [
        slideshowPhotoId,
        isReady,
        isSlideshowResponse,
        response,
        watermarkUrl,
        volume,
        isMuted,
        handleAudioActiveChange,
        isSilent
    ]);

    return {
        coverSlideshow,
        isSettled: slideshowPhotoId === null || isReady || hasFailed
    };
}

/**
 * Stand-ins for when there are no client-gallery services (so no slideshow is fetched anyway) —
 * `useClientSlideshow` needs these to exist, but with a null photoId it never calls them.
 */
const EMPTY_AUTHORIZATION_TOKEN = {
    getAuthorizationTokenForEvent: () => Promise.resolve('')
};

const EMPTY_EVENT_BUS = {
    emit: () => undefined,
    on: () => () => undefined
} as unknown as SpAngularJs.ISpEventBus;

export default useCoverSlideshowLoader;
